import { normalizeLabelCode } from '@barso/shared';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { ScanGate } from '../pos/camera';

const STORAGE_KEY = 'barso.autoCapture';
/**
 * Ruhezeit nach der zehnten Ziffer: Ein längerer Code (EAN-13 oder anderer fremder Code, Regel 4) darf auch beim langsamen
 * Tippen nicht nach der zehnten Ziffer abgeschnitten werden. Ein Handscanner mit Enter sendet ohnehin sofort ab.
 */
export const AUTO_SUBMIT_DELAY_MS = 400;
/** Derselbe Code innerhalb dieser Zeit zählt nur einmal (Doppel-Scan-Schutz). */
export const AUTO_SUBMIT_HOLD_MS = 2_000;

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

function writeStored(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, value ? 'on' : 'off');
  } catch {
    // Speicher gesperrt (privates Fenster): der Schalter gilt dann nur für diese Sitzung
  }
}

interface Options {
  /** Aktueller Inhalt des Eingabefelds. */
  value: string;
  /** Leert das Feld. */
  clear: () => void;
  /** Verarbeitet einen erkannten Code (wie ein Klick auf den Knopf). */
  onCode: (code: string) => void;
  /** Das Feld, das nach dem Erfassen wieder den Fokus bekommt. */
  inputRef: RefObject<HTMLInputElement | null>;
  /** `false` sperrt das Erfassen (z. B. Station gesperrt oder Vorgang läuft). */
  enabled?: boolean;
}

/**
 * Erfasst einen Code automatisch, sobald das Feld normalisiert genau zehn Ziffern enthält und 400 ms lang nichts mehr
 * dazukommt (Eingabe per Handscanner oder Tastatur): dann Verarbeitung wie ein Klick, Feld leeren und fokussieren. Nicht bei gruppiertem Pool-Klartext (beginnt mit 99, enthält
 * Trenner): die zwölf Ziffern werden abgewartet. Längere
 * Ziffernfolgen (EAN-13, fremde Codes) werden nie abgeschnitten. Derselbe Code innerhalb von 2 Sekunden wird nur einmal
 * verarbeitet. Die Prüfziffer prüft nicht dieser Hook: Der Startwert der Organisation ist im Browser nicht bekannt, die
 * Prüfung übernehmen Kasse (`evaluateScan`) und Server. Der Schalter gilt je Gerät (localStorage, jeder Zugriff abgesichert).
 */
export function useAutoSubmitCode({ value, clear, onCode, inputRef, enabled = true }: Options) {
  const [auto, setAutoState] = useState(readStored);
  const gate = useRef(new ScanGate(AUTO_SUBMIT_HOLD_MS, false));
  const latest = useRef({ clear, onCode });
  useEffect(() => {
    latest.current = { clear, onCode };
  });

  const setAuto = useCallback((next: boolean) => {
    setAutoState(next);
    writeStored(next);
  }, []);

  const normalized = normalizeLabelCode(value);
  /**
   * Ein gruppierter Pool-Klartext (zwölf Ziffern, beginnt mit 99, mit Leerzeichen oder Bindestrichen) darf nach der
   * zehnten Ziffer nicht als BARSO-Code abgesendet werden. Scanner und BARSO-Codes liefern keine Trenner.
   */
  const poolPlaintext = /^\s*99/.test(value) && /[\s-]/.test(value.trim());
  const tenDigits = /^\d{10}$/.test(normalized) && !poolPlaintext;

  useEffect(() => {
    if (!auto || !enabled || !tenDigits) return;
    const timer = setTimeout(() => {
      const accepted = gate.current.accept(normalized, Date.now());
      latest.current.clear();
      if (accepted !== null) latest.current.onCode(accepted);
      inputRef.current?.focus();
    }, AUTO_SUBMIT_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [auto, enabled, tenDigits, normalized, inputRef]);

  return { auto, setAuto };
}

/** Schalter „Automatisch erfassen“. */
export function AutoCaptureSwitch({
  auto,
  onChange,
}: {
  auto: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="check">
      <input
        type="checkbox"
        checked={auto}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      <span>Automatisch erfassen</span>
    </label>
  );
}
